/**
 * @engine/vfx - Operator Nodes
 *
 * Math, random, and utility nodes available in any context.
 */

import type { VFXNodeDefinition } from '../../types';
import { dataPort } from '../../types';

/**
 * Operator node definitions.
 */

export const POINTMAP_NODES: readonly VFXNodeDefinition[] = [

  {
    id: 'vfx.operator.sample_point_map',
    name: 'Sample Point Map',
    category: 'sampling',
    contexts: [],
    description: 'Sample vec3 attribute (position/normal) from a point map texture',
    color: '#E91E63',
    inputs: [
      dataPort('pointMap', 'Point Map', 'texture2d'),
      dataPort('pointCount', 'Point Count', 'uint', { defaultValue: 0 }),
      dataPort('index', 'Index', 'uint', { defaultValue: 0 }),
      dataPort('mode', 'Mode', 'int', { defaultValue: 0, options: ['Wrap', 'Clamp', 'Mirror'] }),
    ],
    outputs: [
      dataPort('value', 'Value', 'vec3'),
    ],
    shader: {
      code: `
        let dims = textureSize(pointMap);
        let texSize = dims.x * dims.y;
        let count = (pointCount > 0u ? min(pointCount, texSize) : texSize);

        // Apply addressing mode
        uint i = index;
        if (mode == 0) {
          i = index % count; // Wrap
        } else if (mode == 1) {
          i = min(index, count - 1u); // Clamp
        } else {
          let cycle = index / count;
          let pos = index % count;
          i = ((cycle & 1u) == 1u ? count - 1u - pos : pos); // Mirror
        }

        // Convert linear index to UV
        let y = i / dims.x;
        let x = i - (y * dims.x);

        let texel = pointMap.Load(int3(int(x), int(y), 0));
        value = texel.xyz;
      `,
    },
  },

  {
    id: 'vfx.operator.sample_point_map_color',
    name: 'Sample Point Map Color',
    category: 'sampling',
    contexts: [],
    description: 'Sample vec4 attribute (color) from a point map texture',
    color: '#E91E63',
    inputs: [
      dataPort('pointMap', 'Point Map', 'texture2d'),
      dataPort('pointCount', 'Point Count', 'uint', { defaultValue: 0 }),
      dataPort('index', 'Index', 'uint', { defaultValue: 0 }),
      dataPort('mode', 'Mode', 'int', { defaultValue: 0, options: ['Wrap', 'Clamp', 'Mirror'] }),
    ],
    outputs: [
      dataPort('color', 'Color', 'color'),
    ],
    shader: {
      code: `
        let dims = textureSize(pointMap);
        let texSize = dims.x * dims.y;
        let count = (pointCount > 0u ? min(pointCount, texSize) : texSize);

        // Apply addressing mode
        uint i = index;
        if (mode == 0) {
          i = index % count; // Wrap
        } else if (mode == 1) {
          i = min(index, count - 1u); // Clamp
        } else {
          let cycle = index / count;
          let pos = index % count;
          i = ((cycle & 1u) == 1u ? count - 1u - pos : pos); // Mirror
        }

        // Convert linear index to UV
        let y = i / dims.x;
        let x = i - (y * dims.x);

        color = pointMap.Load(int3(int(x), int(y), 0));
      `,
    },
  },

  {
    id: 'vfx.operator.sample_point_map_random',
    name: 'Sample Point Map Random',
    category: 'sampling',
    contexts: [],
    description: 'Sample random point from a point map (deterministic per-particle)',
    color: '#E91E63',
    inputs: [
      dataPort('pointMap', 'Point Map', 'texture2d'),
      dataPort('pointCount', 'Point Count', 'uint', { defaultValue: 0 }),
      dataPort('seed', 'Seed', 'uint', { defaultValue: 0 }),
    ],
    outputs: [
      dataPort('value', 'Value', 'vec3'),
      dataPort('index', 'Index', 'uint'),
    ],
    shader: {
      code: `
        let dims = textureSize(pointMap);
        let texSize = dims.x * dims.y;
        let count = (pointCount > 0u ? min(pointCount, texSize) : texSize);

        // Deterministic random index based on particle seed
        let rand = hash(particle.seed + seed);
        index = uint(rand * float(count));

        // Convert linear index to UV
        let y = index / dims.x;
        let x = index - (y * dims.x);

        let texel = pointMap.Load(int3(int(x), int(y), 0));
        value = texel.xyz;
      `,
    },
  },
];
